import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Mail, Plus, Trash2, RefreshCw, CheckCircle2, AlertCircle, Lock, ExternalLink,
  Server, User as UserIcon, Loader2, Inbox, Sparkles, BrainCircuit, Settings2, History,
  ListChecks, CalendarDays,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import TasksSyncCard from '../components/perfil/TasksSyncCard';
import { req } from '../services/api';

// Iter58 — usamos `req` exportado de services/api.js (DRY: antes había def local idéntica)

export default function Perfil() {
  const { user, hasPermission } = useAuth();
  const tieneCorreos = hasPermission('correos', 'leer');
  const [params, setParams] = useSearchParams();
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImap, setShowImap] = useState(false);
  const [bandeja, setBandeja] = useState({ open: false, items: [], cuenta: null, loading: false });
  const [syncCfg, setSyncCfg] = useState({ open: false, cuenta: null });

  // Mensajes desde callback OAuth
  useEffect(() => {
    const ok = params.get('correo_conectado');
    const err = params.get('correo_error');
    if (ok) {
      toast.success(`Cuenta ${ok === 'microsoft' ? 'Microsoft 365' : 'Gmail'} conectada`);
      setParams({});
    }
    if (err) {
      toast.error(`Error OAuth: ${decodeURIComponent(err)}`);
      setParams({});
    }
  }, [params, setParams]);

  const cargar = async () => {
    if (!tieneCorreos) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await req('/api/correo/cuentas');
      setCuentas(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, [tieneCorreos]);

  const conectarOAuth = async (provider) => {
    try {
      const r = await req(`/api/correo/${provider}/start`, { method: 'POST' });
      window.location.href = r.authorization_url;
    } catch (e) { toast.error(e.message); }
  };

  const desconectar = async (cuentaId, email) => {
    if (!window.confirm(`¿Desconectar ${email}? Las credenciales se borrarán del CRM.`)) return;
    try {
      await req(`/api/correo/cuentas/${cuentaId}`, { method: 'DELETE' });
      toast.success('Cuenta desconectada');
      cargar();
    } catch (e) { toast.error(e.message); }
  };

  const verBandeja = async (cuenta) => {
    setBandeja({ open: true, items: [], cuenta, loading: true });
    try {
      const r = await req(`/api/correo/cuentas/${cuenta.id}/mensajes?top=25`);
      setBandeja({ open: true, items: r.items || [], cuenta, loading: false });
    } catch (e) {
      toast.error(e.message);
      setBandeja({ open: false, items: [], cuenta: null, loading: false });
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 space-y-6" data-testid="perfil-page">
      <div>
        <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight flex items-center gap-2">
          <UserIcon className="w-7 h-7 text-brand-500" />
          Mi perfil
        </h1>
        <p className="text-sm text-ink-500 mt-1">
          {user?.email} · {user?.rol}
        </p>
      </div>

      {/* Sección correos */}
      {tieneCorreos && (
      <Card className="p-5" data-testid="seccion-correos">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-ink-900 flex items-center gap-2">
              <Mail className="w-5 h-5 text-brand-600" /> Cuentas de correo conectadas
            </h2>
            <p className="text-xs text-ink-500 mt-1">
              Conecta tu correo personal o corporativo. Los mensajes se vincularán automáticamente
              a empresas y contactos del CRM por dominio/email.
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={cargar} disabled={loading} data-testid="refresh-cuentas">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {loading ? (
          <div className="text-sm text-ink-500 py-8 text-center"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Cargando…</div>
        ) : cuentas.length === 0 ? (
          <div className="text-sm text-ink-500 py-6 text-center bg-ink-50/40 rounded border border-dashed border-ink-200">
            No hay cuentas conectadas todavía.
          </div>
        ) : (
          <div className="space-y-2 mb-4" data-testid="cuentas-lista">
            {cuentas.map((c) => (
              <CuentaRow key={c.id} cuenta={c} onDesconectar={desconectar} onVerBandeja={verBandeja} onConfigurarSync={(cuenta) => setSyncCfg({ open: true, cuenta })} />
            ))}
          </div>
        )}

        {/* Botones para añadir cuenta */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3 border-t border-ink-100">
          <button
            onClick={() => conectarOAuth('microsoft')}
            className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-blue-200 bg-blue-50/40 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition text-sm font-medium text-blue-900"
            data-testid="conectar-microsoft"
          >
            <svg className="w-5 h-5" viewBox="0 0 21 21" fill="currentColor"><path d="M10 0H0v10h10V0zm11 0H11v10h10V0zM10 11H0v10h10V11zm11 0H11v10h10V11z"/></svg>
            Conectar Microsoft 365
          </button>
          <button
            onClick={() => conectarOAuth('google')}
            className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-rose-200 bg-rose-50/40 rounded-lg hover:bg-rose-50 hover:border-rose-300 transition text-sm font-medium text-rose-900"
            data-testid="conectar-google"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Conectar Gmail
          </button>
          <button
            onClick={() => setShowImap(true)}
            className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-ink-200 bg-white rounded-lg hover:bg-ink-50 transition text-sm font-medium text-ink-700"
            data-testid="conectar-imap"
          >
            <Server className="w-5 h-5" />
            Conectar IMAP/SMTP
          </button>
        </div>

        <p className="text-[11px] text-ink-400 mt-3 flex items-start gap-1.5">
          <Lock className="w-3 h-3 mt-0.5 shrink-0" />
          Los tokens OAuth y contraseñas IMAP se cifran con Fernet (AES-128) antes de guardarse en MongoDB. Solo tú puedes ver tus cuentas.
        </p>
      </Card>
      )}

      {tieneCorreos && showImap && <ImapDialog onClose={() => setShowImap(false)} onSaved={() => { setShowImap(false); cargar(); }} />}
      {tieneCorreos && bandeja.open && <BandejaDialog data={bandeja} onClose={() => setBandeja({ open: false, items: [], cuenta: null, loading: false })} />}
      {tieneCorreos && syncCfg.open && <SyncConfigDialog cuenta={syncCfg.cuenta} onClose={() => setSyncCfg({ open: false, cuenta: null })} onChanged={() => { setSyncCfg({ open: false, cuenta: null }); cargar(); }} />}

      {/* Sección estilo IA aprendido */}
      {tieneCorreos && <EstiloAprendidoCard hayCuentas={cuentas.length > 0} />}

      {/* Sección preferencias IA correo (Sprint 4) */}
      {tieneCorreos && <PreferenciasIACorreoCard hayCuentas={cuentas.length > 0} />}

      {/* Sección sync tareas con Microsoft To Do / Google Tasks (Iter56) */}
      {tieneCorreos && cuentas.some((c) => ['microsoft', 'google'].includes(c.tipo)) && <TasksSyncCard />}
    </div>
  );
}

const PreferenciasIACorreoCard = ({ hayCuentas }) => {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await req('/api/auth/me/prefs');
      setPrefs(r);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const togglePref = async (campo, valor) => {
    setSaving(true);
    try {
      const r = await req('/api/auth/me/prefs', {
        method: 'PUT',
        body: JSON.stringify({ [campo]: valor }),
      });
      setPrefs(r);
      toast.success('Preferencia actualizada');
    } catch (e) { toast.error(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  return (
    <Card className="p-5" data-testid="prefs-ia-correo-card">
      <h2 className="text-lg font-semibold text-ink-900 flex items-center gap-2 mb-1">
        <Sparkles className="w-5 h-5 text-violet-600" /> Preferencias IA Correo
      </h2>
      <p className="text-xs text-ink-500 mb-4">
        Activa o desactiva las funcionalidades de IA en tu bandeja. Útil si recibes mucho volumen
        y prefieres reducir el ruido de los avisos automáticos.
      </p>
      {loading || !prefs ? (
        <div className="py-6 text-center text-ink-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Cargando…</div>
      ) : (
        <div className="space-y-3">
          <PreferenciaToggle
            label="Detección de oportunidades en bandeja"
            descripcion="Marca con un badge 🎯 los correos que parecen oportunidades comerciales (presupuestos, cierres, urgencias). Activa por defecto."
            checked={prefs.deteccion_oportunidades_activa}
            onChange={(v) => togglePref('deteccion_oportunidades_activa', v)}
            disabled={saving || !hayCuentas}
            testid="pref-deteccion-oportunidades"
          />
          <PreferenciaToggle
            label="Memorias relevantes al sugerir respuestas"
            descripcion="Cuando pides borradores de respuesta, busca correos similares de tu histórico para enriquecer el contexto. Activa por defecto."
            checked={prefs.memorias_relevantes_activa}
            onChange={(v) => togglePref('memorias_relevantes_activa', v)}
            disabled={saving || !hayCuentas}
            testid="pref-memorias-relevantes"
          />
          <PreferenciaToggle
            label="Vista Gmail (panel partido) en /correos"
            descripcion="Activa la nueva vista estilo Gmail/Outlook (lista + lectura). Desactiva para volver al layout clásico con modal de lectura."
            checked={prefs.vista_correos_gmail}
            onChange={(v) => togglePref('vista_correos_gmail', v)}
            disabled={saving}
            testid="pref-vista-gmail"
          />

          <div className="mt-4 p-3 border border-violet-200 rounded-lg bg-violet-50">
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-violet-900">Reindexar correos ahora</div>
                <div className="text-xs text-violet-700 mt-0.5">Fuerza el pipeline vectorial manualmente sin esperar al cron de 15 min (máx 200 correos).</div>
              </div>
              <ReindexButton disabled={!hayCuentas} />
            </div>
          </div>
          {!hayCuentas && (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
              Conecta primero una cuenta de correo para que estos toggles tengan efecto.
            </p>
          )}
        </div>
      )}
    </Card>
  );
};

const ReindexButton = ({ disabled }) => {
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    setLoading(true);
    try {
      const r = await req('/api/correo/ai/vectorial/reindex?limit=200', { method: 'POST' });
      toast.success(`Indexados ${r.indexados || 0} correo(s) · ${r.chunks || 0} chunks${r.errores ? ` · ${r.errores} errores` : ''}${r.skip ? ` · ${r.skip}` : ''}`);
    } catch (e) { toast.error(`Reindex: ${e.message}`); }
    finally { setLoading(false); }
  };
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={onClick}
      disabled={disabled || loading}
      data-testid="btn-reindex-correos"
      className="shrink-0 bg-white border-violet-300 text-violet-700 hover:bg-violet-100"
    >
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4 mr-1.5" /> Reindexar</>}
    </Button>
  );
};

const PreferenciaToggle = ({ label, descripcion, checked, onChange, disabled, testid }) => (
  <div className="flex items-start justify-between gap-3 p-3 border border-ink-100 rounded-lg hover:bg-ink-50/40 transition">
    <div className="min-w-0 flex-1">
      <div className="font-medium text-sm text-ink-900">{label}</div>
      <div className="text-xs text-ink-500 mt-0.5">{descripcion}</div>
    </div>
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      data-testid={testid}
      className={`relative shrink-0 inline-flex h-6 w-11 items-center rounded-full transition ${checked ? 'bg-violet-600' : 'bg-ink-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  </div>
);

const EstiloAprendidoCard = ({ hayCuentas }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerando, setRegenerando] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const r = await req('/api/correo/ai/estilo');
      setData(r);
    } catch (e) {
      console.error(e);
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const regenerar = async () => {
    setRegenerando(true);
    try {
      const r = await req('/api/correo/ai/estilo/regenerar', { method: 'POST' });
      if (r.estado === 'ok') toast.success(`Estilo aprendido (${r.correos_analizados} correos)`);
      else if (r.estado === 'insuficiente') toast.message('Pocos correos enviados sincronizados aún. Abre tu bandeja en "Ver bandeja" para sincronizar más.');
      await cargar();
    } catch (e) {
      const msg = String(e.message || '');
      if (msg.includes('sin_cuentas')) toast.error('Conecta primero una cuenta de correo arriba.');
      else toast.error(`Error: ${msg}`);
    }
    finally { setRegenerando(false); }
  };

  return (
    <Card className="p-5" data-testid="seccion-estilo-ia">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg font-semibold text-ink-900 flex items-center gap-2">
            <BrainCircuit className="w-5 h-5 text-violet-600" /> Estilo de redacción aprendido
          </h2>
          <p className="text-xs text-ink-500 mt-1">
            Gemini analiza tus últimos correos enviados y aprende tu estilo (tono, saludos,
            firma…) para que las sugerencias y reescrituras suenen como tú. Se regenera
            automáticamente cada 30 días.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={regenerar} disabled={regenerando || !hayCuentas} data-testid="estilo-regenerar">
          {regenerando ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Aprendiendo…</> : <><Sparkles className="w-4 h-4 mr-1.5" /> Regenerar ahora</>}
        </Button>
      </div>

      {!hayCuentas && (
        <div className="text-sm text-ink-500 py-4 text-center bg-ink-50/40 rounded border border-dashed border-ink-200">
          Conecta primero una cuenta de correo arriba para aprender tu estilo.
        </div>
      )}

      {hayCuentas && loading && (
        <div className="text-sm text-ink-500 py-6 text-center"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Cargando…</div>
      )}

      {hayCuentas && !loading && (!data || !data.presente) && (
        <div className="text-sm text-ink-500 py-4 text-center bg-violet-50/40 rounded border border-dashed border-violet-200" data-testid="estilo-sin-perfil">
          Aún no se ha aprendido tu estilo. Pulsa <span className="font-medium">"Regenerar ahora"</span> o
          espera al job nocturno (04:30) tras sincronizar tu bandeja.
        </div>
      )}

      {hayCuentas && !loading && data && data.presente && (
        <div className="space-y-2 text-sm" data-testid="estilo-perfil">
          {data.estado === 'insuficiente' ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded text-amber-900 text-xs">
              <span className="font-semibold">Datos insuficientes.</span> {data.perfil?.notas_estilisticas}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap gap-2">
                <Badge className="bg-violet-50 text-violet-700 border-violet-200 border">Tono: {data.perfil?.tono}</Badge>
                <Badge className="bg-violet-50 text-violet-700 border-violet-200 border">Formalidad: {data.perfil?.formalidad}</Badge>
                <Badge className="bg-violet-50 text-violet-700 border-violet-200 border">Idioma: {data.perfil?.idioma_principal}</Badge>
                <Badge className="bg-ink-50 text-ink-700 border-ink-200 border">Emojis: {data.perfil?.uso_emojis}</Badge>
                <Badge className="bg-ink-50 text-ink-700 border-ink-200 border">Firma: {data.perfil?.uso_firma}</Badge>
                <Badge className="bg-ink-50 text-ink-700 border-ink-200 border">Long. media: {data.perfil?.longitud_media_palabras || '—'} palabras</Badge>
              </div>
              {(data.perfil?.saludos_frecuentes || []).length > 0 && (
                <div className="text-xs"><span className="font-semibold text-ink-700">Saludos:</span> {data.perfil.saludos_frecuentes.join(' · ')}</div>
              )}
              {(data.perfil?.despedidas_frecuentes || []).length > 0 && (
                <div className="text-xs"><span className="font-semibold text-ink-700">Despedidas:</span> {data.perfil.despedidas_frecuentes.join(' · ')}</div>
              )}
              {data.perfil?.firma_detectada && (
                <div className="text-xs">
                  <div className="font-semibold text-ink-700 mb-1">Firma detectada:</div>
                  <pre className="whitespace-pre-wrap font-sans bg-ink-50 border border-ink-200 rounded p-2 text-ink-700">{data.perfil.firma_detectada}</pre>
                </div>
              )}
              {data.perfil?.notas_estilisticas && (
                <div className="text-xs text-ink-600 italic">"{data.perfil.notas_estilisticas}"</div>
              )}
              <div className="text-[11px] text-ink-400 pt-2 border-t border-ink-100">
                {data.correos_analizados} correos · {data.modelo} · generado {data.generado_at ? new Date(data.generado_at).toLocaleString('es-ES') : '—'}
              </div>
            </>
          )}
        </div>
      )}
    </Card>
  );
};

const CuentaRow = ({ cuenta, onDesconectar, onVerBandeja, onConfigurarSync }) => {
  const tipoLabel = { microsoft: 'Microsoft 365', google: 'Gmail', imap: 'IMAP/SMTP' }[cuenta.tipo] || cuenta.tipo;
  const tipoColor = { microsoft: 'bg-blue-50 text-blue-700 border-blue-200',
                      google: 'bg-rose-50 text-rose-700 border-rose-200',
                      imap: 'bg-ink-100 text-ink-700 border-ink-200' }[cuenta.tipo];
  const cfg = cuenta.sync_config || {};
  const folderLabel = { inbox: 'Recibidos', sent: 'Enviados', both: 'Recibidos+Enviados' }[cfg.folder || 'inbox'];
  const stats = cuenta.sync_stats || {};
  // Iter57: estado del sync unificado (3 fases: correos / tareas / calendario)
  const [syncTodo, setSyncTodo] = useState({ running: false, fase: null, resultado: null });

  const sincronizarTodo = async () => {
    setSyncTodo({ running: true, fase: 'correos', resultado: null });
    try {
      // Mostrar fases progresivamente con timers (UI optimista; el endpoint es secuencial)
      const t1 = setTimeout(() => setSyncTodo((s) => s.running ? { ...s, fase: 'tareas' } : s), 1500);
      const t2 = setTimeout(() => setSyncTodo((s) => s.running ? { ...s, fase: 'calendario' } : s), 3500);
      const r = await req(`/api/correo/cuentas/${cuenta.id}/sincronizar-todo`, { method: 'POST' });
      clearTimeout(t1); clearTimeout(t2);
      setSyncTodo({ running: false, fase: null, resultado: r });
      const f = r.fases || {};
      const okAll = f.correos?.ok && f.tareas?.ok && f.calendario?.ok;
      if (okAll) {
        const correosT = f.correos?.detalle?.total ?? 0;
        const tareasMsg = f.tareas?.detalle?.skip ? 'tareas: skip' : `tareas ${(f.tareas?.detalle?.importadas||0)+(f.tareas?.detalle?.exportadas||0)}`;
        toast.success(`Sync OK · correos ${correosT} · ${tareasMsg} · calendario ✓`);
      } else {
        const errs = Object.entries(f).filter(([_, v]) => v.error).map(([k, v]) => `${k}: ${v.error}`).join('; ');
        toast.warning(`Sync parcial — ${errs || 'algunas fases fallaron'}`);
      }
    } catch (e) {
      setSyncTodo({ running: false, fase: null, resultado: null });
      toast.error(`Sync: ${e.message}`);
    }
  };

  const FASES = [
    { key: 'correos', label: 'Correos', icon: Inbox },
    { key: 'tareas', label: 'Tareas', icon: ListChecks },
    { key: 'calendario', label: 'Calendario', icon: CalendarDays },
  ];

  return (
    <div
      className={`flex flex-col gap-2 p-3 border rounded-lg transition ${cuenta.needs_reauth ? 'border-rose-400 bg-rose-50/40' : 'border-ink-200 hover:border-brand-300'}`}
      data-testid={`cuenta-row-${cuenta.id}`}
    >
      {/* Banner de reconectar (Iter58 auto-recovery) */}
      {cuenta.needs_reauth && (
        <div className="flex items-start gap-2 px-2 py-1.5 bg-rose-100 border border-rose-300 rounded text-xs text-rose-900" data-testid={`needs-reauth-banner-${cuenta.id}`}>
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <strong>Reconexión requerida.</strong>{' '}
            {cuenta.needs_reauth_motivo || 'El proveedor rechazó el refresh del token.'}
            {' '}Pulsa <em>Desconectar</em> y vuelve a conectar la cuenta para reanudar la sincronización.
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
          <div className="min-w-0">
            <div className="font-medium text-ink-800 truncate">{cuenta.email}</div>
            <div className="flex items-center gap-2 text-xs text-ink-500 flex-wrap">
              <Badge className={`text-[10px] border ${tipoColor}`}>{tipoLabel}</Badge>
              {cfg.folder && (
                <Badge className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] border" title={`${cfg.max_dias} días · ${cfg.max_mensajes} max`}>
                  ⚙ {folderLabel} · {cfg.max_dias}d · {cfg.max_mensajes}
                </Badge>
              )}
              {cuenta.ultima_sync && <span>Última sync: {new Date(cuenta.ultima_sync).toLocaleString('es-ES')}</span>}
              {!cuenta.ultima_sync && <span>Sin sincronizar</span>}
              {stats.ultima_sync_resultados && (
                <span className="text-emerald-600">
                  · {Object.entries(stats.ultima_sync_resultados).map(([f, n]) => `${f}:${n}`).join(' / ')}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            size="sm"
            onClick={sincronizarTodo}
            disabled={syncTodo.running}
            data-testid={`sync-todo-${cuenta.id}`}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            title="Sincroniza correos, tareas y calendario en una sola acción"
          >
            {syncTodo.running ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Sincronizar todo
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onConfigurarSync(cuenta)} title="Configurar sincronización" data-testid={`config-sync-${cuenta.id}`} className="text-violet-700 hover:bg-violet-50">
            <Settings2 className="w-4 h-4 mr-1" /> Config
          </Button>
          <Button size="sm" variant="outline" onClick={() => onVerBandeja(cuenta)} data-testid={`ver-bandeja-${cuenta.id}`}>
            <Inbox className="w-4 h-4 mr-1.5" /> Bandeja
          </Button>
          <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={() => onDesconectar(cuenta.id, cuenta.email)} data-testid={`desconectar-${cuenta.id}`}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Feedback progresivo del sync unificado */}
      {(syncTodo.running || syncTodo.resultado) && (
        <div className="flex items-center gap-3 px-2 py-1.5 bg-ink-50 border border-ink-200 rounded text-xs" data-testid={`sync-todo-progress-${cuenta.id}`}>
          {FASES.map((f, idx) => {
            const fase = syncTodo.resultado?.fases?.[f.key];
            const isCurrent = syncTodo.running && syncTodo.fase === f.key;
            const isPast = syncTodo.running && FASES.findIndex((x) => x.key === syncTodo.fase) > idx;
            const status = fase ? (fase.ok ? 'ok' : 'error') : (isCurrent ? 'doing' : isPast ? 'past' : 'pending');
            const Icon = f.icon;
            const color = status === 'ok' ? 'text-emerald-600' : status === 'error' ? 'text-rose-600' : status === 'doing' ? 'text-brand-600 animate-pulse' : 'text-ink-300';
            return (
              <div key={f.key} className={`flex items-center gap-1 ${color}`} data-testid={`sync-fase-${f.key}-${cuenta.id}`}>
                {status === 'doing' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
                <span>{f.label}</span>
                {status === 'ok' && <CheckCircle2 className="w-3 h-3" />}
                {status === 'error' && <AlertCircle className="w-3 h-3" />}
                {fase?.detalle?.total !== undefined && <span className="font-mono">·{fase.detalle.total}</span>}
                {fase?.detalle?.importadas !== undefined && <span className="font-mono">·↓{fase.detalle.importadas}↑{fase.detalle.exportadas||0}</span>}
              </div>
            );
          })}
          {syncTodo.resultado && !syncTodo.running && (
            <button onClick={() => setSyncTodo({ running: false, fase: null, resultado: null })} className="ml-auto text-ink-400 hover:text-ink-600" title="Cerrar">✕</button>
          )}
        </div>
      )}
    </div>
  );
};

const SyncConfigDialog = ({ cuenta, onClose, onChanged }) => {
  const cfg0 = cuenta?.sync_config || {};
  const [folder, setFolder] = useState(cfg0.folder || 'inbox');
  const [maxDias, setMaxDias] = useState(cfg0.max_dias || 90);
  const [maxMsg, setMaxMsg] = useState(cfg0.max_mensajes || 500);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState(null);

  const guardarSolo = async () => {
    setSaving(true);
    try {
      await req(`/api/correo/cuentas/${cuenta.id}/sync-config`, {
        method: 'PUT',
        body: JSON.stringify({ folder, max_dias: maxDias, max_mensajes: maxMsg }),
      });
      toast.success('Configuración guardada');
      onChanged();
    } catch (e) { toast.error(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  const sincronizarAhora = async () => {
    setSyncing(true);
    setResult(null);
    try {
      const r = await req(`/api/correo/cuentas/${cuenta.id}/sincronizar`, {
        method: 'POST',
        body: JSON.stringify({ folder, max_dias: maxDias, max_mensajes: maxMsg, persistir_config: true }),
      });
      setResult(r);
      toast.success(`Sincronizados ${r.total_persistidos} mensaje(s)`);
    } catch (e) { toast.error(`Sync: ${e.message}`); }
    finally { setSyncing(false); }
  };

  const opciones_dias = [30, 90, 180, 365, 730];
  const opciones_max = [100, 500, 1000, 2000];

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} data-testid="dialog-sync-config">
        <h3 className="text-lg font-semibold text-ink-900 mb-1 flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-violet-600" /> Configurar sincronización
        </h3>
        <p className="text-xs text-ink-500 mb-4 truncate">
          {cuenta?.email} · {cuenta?.tipo}
        </p>

        <div className="space-y-4">
          <div>
            <label className="text-xs text-ink-500 uppercase tracking-wider">Carpetas a sincronizar</label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {[['inbox', 'Recibidos'], ['sent', 'Enviados'], ['both', 'Ambas']].map(([k, l]) => (
                <button
                  key={k}
                  onClick={() => setFolder(k)}
                  className={`px-3 py-2 rounded border text-sm transition ${folder === k ? 'border-violet-400 bg-violet-50 text-violet-700 font-medium' : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-50'}`}
                  data-testid={`sync-folder-${k}`}
                >
                  {l}
                </button>
              ))}
            </div>
            {folder === 'sent' || folder === 'both' ? (
              <p className="text-[11px] text-violet-700 mt-1.5">
                💡 Sincronizar enviados es necesario para que la IA aprenda tu estilo de redacción.
              </p>
            ) : null}
          </div>

          <div>
            <label className="text-xs text-ink-500 uppercase tracking-wider">Antigüedad máxima</label>
            <div className="flex gap-1.5 mt-1.5 flex-wrap">
              {opciones_dias.map((d) => (
                <button
                  key={d}
                  onClick={() => setMaxDias(d)}
                  className={`px-3 py-1.5 rounded border text-sm transition ${maxDias === d ? 'border-violet-400 bg-violet-50 text-violet-700 font-medium' : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-50'}`}
                  data-testid={`sync-dias-${d}`}
                >
                  {d < 365 ? `${d}d` : `${Math.round(d / 365)}a`}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-ink-500 uppercase tracking-wider">Máx. mensajes por carpeta</label>
            <div className="flex gap-1.5 mt-1.5 flex-wrap">
              {opciones_max.map((m) => (
                <button
                  key={m}
                  onClick={() => setMaxMsg(m)}
                  className={`px-3 py-1.5 rounded border text-sm transition ${maxMsg === m ? 'border-violet-400 bg-violet-50 text-violet-700 font-medium' : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-50'}`}
                  data-testid={`sync-max-${m}`}
                >
                  {m}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-ink-400 mt-1.5">
              Estos límites se aplican por carpeta. "Ambas" trae hasta {maxMsg} de cada una.
            </p>
          </div>

          {result && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-xs" data-testid="sync-resultado">
              <div className="font-semibold text-emerald-900 mb-1">Sincronización completada</div>
              <div className="text-emerald-700">
                {Object.entries(result.resultados || {}).map(([f, n]) => (
                  <div key={f}>· {f}: {n} mensaje(s) persistido(s)</div>
                ))}
                {Object.entries(result.errores || {}).map(([f, e]) => (
                  <div key={f} className="text-rose-700">· Error en {f}: {e}</div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} disabled={saving || syncing}>Cerrar</Button>
          <Button variant="outline" onClick={guardarSolo} disabled={saving || syncing} data-testid="sync-guardar">
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Guardando…</> : 'Guardar config'}
          </Button>
          <Button onClick={sincronizarAhora} disabled={saving || syncing} className="bg-violet-600 hover:bg-violet-700" data-testid="sync-ahora">
            {syncing ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sincronizando…</> : <><History className="w-4 h-4 mr-1.5" /> Sincronizar ahora</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ImapDialog = ({ onClose, onSaved }) => {
  const [form, setForm] = useState({
    email: '', imap_host: '', imap_port: 993, smtp_host: '', smtp_port: 465,
    username: '', password: '', use_tls: true,
  });
  const [saving, setSaving] = useState(false);

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const guardar = async () => {
    setSaving(true);
    try {
      await req('/api/correo/imap/conectar', {
        method: 'POST',
        body: JSON.stringify({ ...form, imap_port: Number(form.imap_port), smtp_port: Number(form.smtp_port) }),
      });
      toast.success('Cuenta IMAP conectada');
      onSaved();
    } catch (e) { toast.error(`Conexión falló: ${e.message}`); }
    finally { setSaving(false); }
  };

  const presets = {
    hostinger: { imap_host: 'imap.hostinger.com', imap_port: 993, smtp_host: 'smtp.hostinger.com', smtp_port: 465, use_tls: true },
    yahoo: { imap_host: 'imap.mail.yahoo.com', imap_port: 993, smtp_host: 'smtp.mail.yahoo.com', smtp_port: 465, use_tls: true },
    gmail_imap: { imap_host: 'imap.gmail.com', imap_port: 993, smtp_host: 'smtp.gmail.com', smtp_port: 465, use_tls: true },
    office365: { imap_host: 'outlook.office365.com', imap_port: 993, smtp_host: 'smtp.office365.com', smtp_port: 587, use_tls: true },
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl max-w-lg w-full p-5" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} data-testid="dialog-imap">
        <h3 className="text-lg font-semibold text-ink-900 mb-1 flex items-center gap-2">
          <Server className="w-5 h-5" /> Conectar IMAP/SMTP
        </h3>
        <p className="text-xs text-ink-500 mb-4">
          Para Hostinger, Yahoo, cuentas corporativas o cualquier proveedor con IMAP/SMTP.
        </p>

        <div className="flex flex-wrap gap-2 mb-3 text-xs items-center">
          <span className="text-ink-500">Atajos:</span>
          {Object.entries(presets).map(([k, p]) => (
            <button
              key={k}
              onClick={() => setForm((f) => ({ ...f, ...p }))}
              className="px-2 py-0.5 rounded bg-brand-50 text-brand-700 border border-brand-200 hover:bg-brand-100"
              data-testid={`imap-preset-${k}`}
            >
              {k === 'office365' ? 'Office 365' : k.replace('_', ' ')}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs text-ink-500">Email</label>
            <Input type="email" value={form.email} onChange={(e) => setF('email', e.target.value)} placeholder="usuario@empresa.com" data-testid="imap-email" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="text-xs text-ink-500">Servidor IMAP</label>
              <Input value={form.imap_host} onChange={(e) => setF('imap_host', e.target.value)} placeholder="imap.proveedor.com" data-testid="imap-host" />
            </div>
            <div>
              <label className="text-xs text-ink-500">Puerto</label>
              <Input type="number" value={form.imap_port} onChange={(e) => setF('imap_port', e.target.value)} data-testid="imap-port" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <label className="text-xs text-ink-500">Servidor SMTP</label>
              <Input value={form.smtp_host} onChange={(e) => setF('smtp_host', e.target.value)} placeholder="smtp.proveedor.com" data-testid="smtp-host" />
            </div>
            <div>
              <label className="text-xs text-ink-500">Puerto</label>
              <Input type="number" value={form.smtp_port} onChange={(e) => setF('smtp_port', e.target.value)} data-testid="smtp-port" />
            </div>
          </div>
          <div>
            <label className="text-xs text-ink-500">Usuario (normalmente igual al email)</label>
            <Input value={form.username} onChange={(e) => setF('username', e.target.value)} data-testid="imap-username" />
          </div>
          <div>
            <label className="text-xs text-ink-500">Contraseña</label>
            <Input type="password" value={form.password} onChange={(e) => setF('password', e.target.value)} data-testid="imap-password" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.use_tls} onChange={(e) => setF('use_tls', e.target.checked)} data-testid="imap-tls" />
            Usar TLS/SSL (recomendado)
          </label>
        </div>

        <div className="mt-4 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-900 flex items-start gap-2">
          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
          La contraseña se guardará cifrada en el CRM. En Yahoo/Gmail crea una contraseña de aplicación.
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving || !form.email || !form.imap_host || !form.password} data-testid="imap-guardar">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Probando login…</> : 'Probar y guardar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const BandejaDialog = ({ data, onClose }) => {
  const { items, cuenta, loading } = data;
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} data-testid="dialog-bandeja">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Inbox className="w-5 h-5" /> Últimos 25 mensajes
            </h3>
            <p className="text-xs text-ink-500">{cuenta?.email} · {cuenta?.tipo}</p>
          </div>
          <Button variant="ghost" onClick={onClose}>✕</Button>
        </div>
        {loading ? (
          <div className="py-8 text-center text-ink-500"><Loader2 className="w-5 h-5 inline animate-spin mr-2" />Cargando…</div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-ink-500">Bandeja vacía.</div>
        ) : (
          <div className="space-y-1">
            {items.map((m) => <MensajeRow key={m.id} m={m} />)}
          </div>
        )}
      </div>
    </div>
  );
};

const MensajeRow = ({ m }) => (
  <div
    className={`p-2 border-b border-ink-100 hover:bg-ink-50/60 ${!m.is_read ? 'bg-blue-50/40' : ''}`}
    data-testid={`mensaje-${m.id}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 text-sm">
          <span className="font-medium truncate">{m.from_name || m.from_address}</span>
          {m.vinculado?.entidad_id && (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[9px]">
              ↳ vinculado
            </Badge>
          )}
        </div>
        <div className="text-xs text-ink-700 truncate">{m.subject || '(sin asunto)'}</div>
        <div className="text-[11px] text-ink-400 truncate">{m.preview}</div>
      </div>
      <div className="text-[10px] text-ink-400 shrink-0">
        {m.received ? new Date(m.received).toLocaleDateString('es-ES') : ''}
      </div>
    </div>
  </div>
);
